import type { FC, ReactNode } from 'react'

import { Clock, Layers, MessageSquare, Zap } from 'lucide-react'

const benefits = [
    {
        title: 'One point of contact',
        text: "No dashboards to juggle and no prompts to chain together. You brief the Coordinator and it handles every handoff.",
        icon: MessageSquare,
        color: '#a78bfa'
    },
    {
        title: 'Specialists, not generalists',
        text: "Each agent does one job well, from SEO to testing, so quality holds up as the workload grows.",
        icon: Layers,
        color: '#e879f9'
    },
    {
        title: 'Teams work at the same time',
        text: "Marketing, leads, email and bug fixes move in parallel instead of waiting in line for each other.",
        icon: Zap,
        color: '#22d3ee'
    },
    {
        title: 'Always on',
        text: "Your agents keep going at night and on weekends, and the Coordinator has a summary ready when you are back.",
        icon: Clock,
        color: '#34d399'
    }
]

const BenefitsSection: FC = (): ReactNode => {
    return (
        <section id='benefits' className='py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='mx-auto max-w-2xl text-center'>
                    <p className='font-mono text-xs uppercase tracking-[0.2em] text-primary'>
                        Why it works
                    </p>
                    <h2 className='mt-4 font-heading text-3xl font-semibold tracking-tight text-balance sm:text-5xl'>
                        A whole growth team, without the managing
                    </h2>
                </div>

                <div className='mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4'>
                    {benefits.map((benefit) => {
                        const Icon = benefit.icon

                        return (
                            <article
                                key={benefit.title}
                                className='group rounded-2xl border border-white/10 bg-card/60 p-6 transition-colors hover:border-white/20 hover:bg-card'
                            >
                                <span
                                    className='flex size-11 items-center justify-center rounded-xl'
                                    style={{ backgroundColor: `${benefit.color}1a`, color: benefit.color }}
                                >
                                    <Icon className='size-5' aria-hidden='true' />
                                </span>

                                <h3 className='mt-5 font-heading text-lg font-semibold'>
                                    {benefit.title}
                                </h3>
                                <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                                    {benefit.text}
                                </p>
                            </article>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}

export default BenefitsSection
